import { useState, useEffect, useRef, useCallback } from 'react';
/* No OverlayTrigger/Tooltip: react-bootstrap draws its tooltip with
   .tooltip-inner and .tooltip-arrow, both of which came from the
   Bootstrap stylesheet this project deleted -- so every tooltip on this
   page rendered nothing at all. The kit's CSS tooltip (.pg-tip +
   data-tip, TrainerDashboard.css) is used instead. */
import { Modal, Form } from 'react-bootstrap';
import {
  FaGraduationCap,
  FaUsers,
  FaCalendarAlt,
  FaEye,
  FaVideo,
  FaTrophy,
  FaRocket,
  FaSearch,
  FaTimes,
  FaInfoCircle,
  FaUserTie,
  FaLink,
  FaBookmark,
  FaEdit,
  FaTrash,
  FaClock,
  FaComments,
  FaLaptopCode,
  FaCheckCircle,
  FaTimesCircle,
  FaAward,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';
import { confirm } from '../common/Confirm/confirm';
import { getMe } from '../../../services/AuthService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from '../common/Toast/toast';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/AxiosInstance';
import { getTrainerOptions, getTrainerByUserId } from '../../../services/TrainerService';
import { getMyInterviewEvents, getMockInterviewResultsByEvent } from '../../../services/MockInterview';
import { deleteEvent } from '../../../services/EventService';
import NewCustomAsyncSelectDropdown from '../common/NewCustomAsyncSelectDropdown.jsx';
import useScrollLoadMore from '../common/useScrollLoadMore';
import StudentDetailModal from './StudentDetailModal.jsx';
import PopupDone, { usePopupDone } from '../common/PopupDone/PopupDone.jsx';
import moment from 'moment';
/* The shared page kit -- shell, gate, filter strip, buttons, states --
   the same one /mock-interviews and /student-quizzes use, so this page
   reads as part of the same app. */
import '../Trainer/TrainerDashboard.css';
import AddEventModal from '../AppMenu/Event_Form/AddEventForm.jsx';
import '../common/lms-pages.css';
import './MockInterviewPage.css';
/* Waypoint -- the bridge. This popup names a batch, a course, trainers
   and a roster of students; every one of those is a record the reader
   will want to follow, and none of them is a page this file should have
   to know anything about. */
import { useWaypoint } from '../common/waypoint/WaypointProvider.jsx';
import '../common/waypoint/waypoint.css';

/* A card's identity. The list holds one row per event PER BATCH, so the
   event id repeats; the server stamps each row with rowKey (event:batch).
   The fallback covers a server that predates it. */
const eventRowKey = (row) => {
  if (row?.rowKey) return row.rowKey;
  const batch = Array.isArray(row?.batchId) ? row.batchId[0]?.id : row?.batchId;
  return `${row?.id}:${batch || ''}`;
};

const MockInterview = () => {
  const { open: openEntity } = useWaypoint();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Trainer identity
  const [actualTrainerId, setActualTrainerId] = useState(null);
  const [trainerName, setTrainerName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // Admin trainer dropdown (paginated async)
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [selectedTrainerOption, setSelectedTrainerOption] = useState(null);
  const [trainerPage, setTrainerPage] = useState(1);
  const [trainerHasMore, setTrainerHasMore] = useState(true);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [trainerSearch, setTrainerSearch] = useState('');
  const [trainerHasLoaded, setTrainerHasLoaded] = useState(false);

  // Mock interview events (server-paginated + server-searched)
  const [mockInterviewEvents, setMockInterviewEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasNextPage: false, hasPrevPage: false });
  const searchDebounceRef = useRef(null);
  // A scroll batch, as opposed to a fresh load: the cards already on
  // screen stay and the new ones land under them, so the page-wide
  // loading state must not appear for it.
  const [loadingMore, setLoadingMore] = useState(false);
  // Page 1 replaces the list; every page after it appends. A ref because
  // the fetch reads it after an await, when a re-render may have moved on.
  const appendRef = useRef(false);
  // Owned by the fetch, so a request that never runs cannot strand it.
  const isFetching = useRef(false);
  // The sentinel under the list -- crossing it asks for the next batch.
  const listEndRef = useRef(null);

  // The event being edited, in a dialog. null = closed.
  const [eventFormId, setEventFormId] = useState(null);
  // Which half of the batch dialog is showing. Reset per batch, not per
  // render, so opening a different session always starts on Details.
  const [batchModalTab, setBatchModalTab] = useState('details');
  // Batch detail modal & embedded students list
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [batchStudentsLoading, setBatchStudentsLoading] = useState(false);
  const [batchStudentsSearch, setBatchStudentsSearch] = useState('');
  const [appliedBatchStudentsSearch, setAppliedBatchStudentsSearch] = useState('');
  const [batchStudentsPage, setBatchStudentsPage] = useState(1);
  const [batchStudentsPerPage, setBatchStudentsPerPage] = useState(10);
  const [totalBatchStudents, setTotalBatchStudents] = useState(0);
  const [loadingMoreStudents, setLoadingMoreStudents] = useState(false);
  const batchStudentsSearchDebounceRef = useRef(null);

  // Student detail modal
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudentData, setSelectedStudentData] = useState(null);

  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const hasFetched = useRef(false);
  const isFetchingTrainers = useRef(false);
  const { hasPermission } = useAuth();

  // ─── Permission guard ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasPermission('mock-interview', 'view', 'lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to access this page.');
        navigate('/');
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // ─── Fetch trainer dropdown (paginated) ─────────────────────────────────────
  const fetchTrainers = useCallback(async (page = 1, search = '', append = false) => {
    if (isFetchingTrainers.current || trainerLoading) return;
    isFetchingTrainers.current = true;
    setTrainerLoading(true);
    try {
      // Names only: a picker needs the id, name and email, nothing else.
      const resp = await getTrainerOptions(20, page, search);
      const trainersData = resp.data || [];
      const pagination = resp.pagination || {};

      const options = trainersData.map((t) => {
        // The trainer record's own id -- what the mock interviews are
        // filed under, so a pick needs no second lookup (see below).
        const id = t.id;
        const name = t.trainerName || '';
        const email = t.trainerEmail || t.traineremail || '';
        const label = email ? `${name} (${email})` : name || String(id || 'Unknown');
        return { value: id, label, data: t };
      }).filter((o) => o.value);

      if (append) {
        setTrainerOptions((prev) => [...prev, ...options]);
      } else {
        setTrainerOptions(options);
      }

      setTrainerHasMore(pagination.hasNext || pagination.hasNextPage || trainersData.length === 20);
      setTrainerPage(page);
    } catch (err) {
      console.error('Failed to fetch trainers:', err);
      toast.error('Failed to load trainers list');
    } finally {
      setTrainerLoading(false);
      isFetchingTrainers.current = false;
    }
  }, [trainerLoading]);

  // ─── Refresh trainers function ─────────────────────────────────────────────
  const handleRefreshTrainers = useCallback(async () => {
    setTrainerSearch('');
    setTrainerPage(1);
    setTrainerHasMore(true);
    setTrainerHasLoaded(false);
    setTrainerOptions([]);
    await fetchTrainers(1, '', false);
  }, [fetchTrainers]);

  // ─── Fetch mock interview events for a given actualTrainerId (server-side
  // paginated + searched) ──────────────────────────────────────────────────
  const fetchMockInterviews = useCallback(async (tid, page = 1, search = '') => {
    if (!tid) return;
    if (isFetching.current) return;
    isFetching.current = true;
    // Read before anything awaits: a ref reset mid-flight would turn an
    // append into a replace and snap the list back to its first page.
    const isAppend = appendRef.current;
    try {
      if (isAppend) setLoadingMore(true);
      else setLoadingEvents(true);
      const resp = await getMyInterviewEvents(tid, page, recordsPerPage, search);
      const rows = resp.data || [];
      // Guarded so a repeated page cannot double up a card. By the row's
      // own key, not the event id: one event for two batches is two rows
      // sharing that id, and the second was being dropped as a repeat.
      if (isAppend) {
        setMockInterviewEvents((prev) => {
          const seen = new Set(prev.map(eventRowKey));
          return [...prev, ...rows.filter((r) => !seen.has(eventRowKey(r)))];
        });
      } else {
        setMockInterviewEvents(rows);
      }
      appendRef.current = false;
      setPagination({
        total: resp.pagination?.total || 0,
        totalPages: resp.pagination?.totalPages || 1,
        hasNextPage: Boolean(resp.pagination?.hasNextPage),
        hasPrevPage: Boolean(resp.pagination?.hasPrevPage),
      });
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch mock interview events:', err);
      toast.error('Failed to load mock interview data');
    } finally {
      setLoadingEvents(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
  }, [recordsPerPage]);

  // ─── Initialise dashboard ───────────────────────────────────────────────────
  const initializeDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const userProfile = await getMe();
      const userData = userProfile.data;

      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      const isAdminUser = profileLabel === 'admin' || userData.isSuperAdmin;
      setIsAdmin(isAdminUser);

      if (isAdminUser) {
        // Admin: just pre-load trainer list; events loaded after trainer selection
        setLoading(false);
        return;
      }

      // Trainer: resolve actual trainer object-id
      const userId = userData.id || userData._id || userData.userId;
      if (!userId) {
        setError('Unable to fetch trainer information');
        setLoading(false);
        return;
      }

      const name = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
      setTrainerName(name);

      try {
        const trainerResp = await getTrainerByUserId(userId);
        if (trainerResp?.data) {
          const tid = trainerResp.data.id || trainerResp.data._id;
          setActualTrainerId(tid);
          await fetchMockInterviews(tid);
        } else {
          setActualTrainerId(userId);
          await fetchMockInterviews(userId);
        }
      } catch {
        setActualTrainerId(userId);
        await fetchMockInterviews(userId);
      }
    } catch (err) {
      console.error('Error in initializeDashboard:', err);
      setError('Failed to initialize dashboard');
    } finally {
      setLoading(false);
    }
  }, [fetchMockInterviews]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      initializeDashboard();
    }
  }, [initializeDashboard]);

  // ─── Handle trainer dropdown open ──────────────────────────────────────────
  const handleTrainerDropdownOpen = useCallback(() => {
    if (!trainerHasLoaded && !trainerLoading && trainerOptions.length === 0) {
      setTrainerHasLoaded(true);
      fetchTrainers(1, '', false);
    }
  }, [trainerHasLoaded, trainerLoading, fetchTrainers, trainerOptions.length]);

  // ─── Handle trainer search with debounce ───────────────────────────────────
  const handleTrainerSearch = useCallback((searchTerm) => {
    setTrainerSearch(searchTerm);
    setTrainerHasLoaded(true);
    setTrainerPage(1);
    setTrainerHasMore(true);
    fetchTrainers(1, searchTerm, false);
  }, [fetchTrainers]);

  // ─── Handle load more trainers on scroll ───────────────────────────────────
  const handleLoadMoreTrainers = useCallback(() => {
    if (trainerHasMore && !trainerLoading && !isFetchingTrainers.current) {
      fetchTrainers(trainerPage + 1, trainerSearch, true);
    }
  }, [trainerHasMore, trainerLoading, fetchTrainers, trainerPage, trainerSearch]);

  // ─── Admin: handle trainer selection ────────────────────────────────────────
  const handleTrainerSelection = useCallback(async (selectedOption) => {
    if (!selectedOption) {
      setSelectedTrainerOption(null);
      setActualTrainerId(null);
      setTrainerName('');
      setMockInterviewEvents([]);
      return;
    }

    setSelectedTrainerOption(selectedOption);
    
    const trainerData = selectedOption.data;
    const name = trainerData?.trainerName || selectedOption.label || 'Unknown Trainer';
    setTrainerName(name);
    
    /* The option's value IS the trainer record's id. The picker used to
       carry the login user id instead, so every pick made a second request
       (getTrainerByUserId) just to turn it back into the record id -- which
       the list row had all along, and which that lookup could get wrong
       when one login has more than one trainer record. */
    const trainerObjectId = selectedOption.value;

    try {
      setActualTrainerId(trainerObjectId);
      await fetchMockInterviews(trainerObjectId);
    } catch (error) {
      console.error('Error loading the trainer\'s mock interviews:', error);
    }
  }, [fetchMockInterviews]);

  /* One dropdown, rendered into the gate before a trainer is picked and
     into the strip afterwards -- the same shape every other "pick
     someone" page on this app uses. */
  const trainerPicker = (
    <NewCustomAsyncSelectDropdown
      options={trainerOptions}
      value={selectedTrainerOption ? [selectedTrainerOption] : []}
      onChange={(selectedOptions) => {
        const opt = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
        handleTrainerSelection(opt);
      }}
      placeholder="Search and select a trainer..."
      isLoading={trainerLoading}
      isMulti={false}
      isSearchable
      onMenuOpen={handleTrainerDropdownOpen}
      onInputChange={handleTrainerSearch}
      onMenuScrollToBottom={handleLoadMoreTrainers}
      onRefresh={handleRefreshTrainers}
    />
  );

  // ─── Batch card click ───────────────────────────────────────────────────────
  const handleBatchClick = useCallback((event) => {
    setSelectedBatch(event);
    setBatchModalTab('details');
    setShowBatchModal(true);
  }, []);

  // ─── Edit / Delete event ────────────────────────────────────────────────────
  // Same underlying event record as the Event List page (/list-event-Form) --
  // same form, same delete endpoint, gated by the same list-event-form
  // permission so visibility matches that page.
  //
  // Edit used to navigate to /event-form/:id, which threw the reader out of
  // the dialog and off the page to edit the thing they were looking at, and
  // left them on the event list afterwards rather than back here. It opens
  // the form in a dialog now -- AddEventModal is the same component
  // /list-event-Form edits through, so the two pages edit an event the same
  // way rather than one routing and one not.
  const handleEditEvent = useCallback((eventId) => {
    // The batch dialog closes first: two stacked dialogs is never what is
    // wanted, and this one reopens from the card behind it.
    setShowBatchModal(false);
    setEventFormId(eventId);
  }, []);

  const handleDeleteEvent = useCallback(async (eventId) => {
    const answer = await confirm({
      title: 'Delete Confirmation',
      text: 'Are you sure you want to delete this mock interview event?',
      tone: 'danger',
      confirmButtonText: 'Yes, Delete it!',
    });
    if (!answer.isConfirmed) return;
    try {
      await deleteEvent(eventId);
      toast.success('Mock interview event deleted successfully');
      appendRef.current = false;
      if (actualTrainerId) await fetchMockInterviews(actualTrainerId, 1, searchTerm);
    } catch (err) {
      console.error('Failed to delete event:', err);
      toast.error('Failed to delete event');
    }
  }, [actualTrainerId, searchTerm, fetchMockInterviews]);
  // ─── Formatting helpers ──────────────────────────────────────────────────────
  const formatTime = (timeString) => moment.utc(timeString).format('hh:mm A');

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });

  const formatEventDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });

  const getModeBadge = (mode) => {
    const modeColors = { Online: 'primary', Offline: 'info', Hybrid: 'warning' };
    return (
      <span>
        {mode}
      </span>
    );
  };

  // ─── Derive batch info from event ────────────────────────────────────────────
  const getBatchFromEvent = (event) => {
    if (!event?.batchId || !Array.isArray(event.batchId) || event.batchId.length === 0) return null;
    return event.batchId[0];
  };

  const getTrainerNameFromEvent = (event) => {
    if (!event?.trainerId || !Array.isArray(event.trainerId) || event.trainerId.length === 0) return 'N/A';
    return event.trainerId.map((t) => t.trainerName).join(', ');
  };

  // Search + pagination are handled server-side (see fetchMockInterviews) --
  // mockInterviewEvents is already just the current page's matching events.
  const now = new Date();
  const activeEvents = mockInterviewEvents.filter(
    (e) => new Date(e.date) >= new Date(now.setHours(0, 0, 0, 0))
  );
  const completedEvents = mockInterviewEvents.filter(
    (e) => new Date(e.date) < new Date(now.setHours(0, 0, 0, 0))
  );

  // ─── Search (debounced, server-side) ───────────────────────────────────────
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      appendRef.current = false;
      if (actualTrainerId) fetchMockInterviews(actualTrainerId, 1, value);
    }, 400);
  }, [actualTrainerId, fetchMockInterviews]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    appendRef.current = false;
    if (actualTrainerId) fetchMockInterviews(actualTrainerId, 1, '');
  }, [actualTrainerId, fetchMockInterviews]);

  // ─── Pagination controls ────────────────────────────────────────────────────
  // The scroll asked for the next batch. Only ever moves forward, and only
  // while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loadingEvents || loadingMore) return;
    if (!actualTrainerId) return;
    if (mockInterviewEvents.length >= pagination.total) return;
    appendRef.current = true;
    // Derived from the cards already on screen rather than counted up, so
    // the batch size can change mid-list without asking for a page that has
    // already been read. 30 loaded at a size of 15 is page 3, not page 2.
    const nextPage = Math.floor(mockInterviewEvents.length / recordsPerPage) + 1;
    fetchMockInterviews(actualTrainerId, nextPage, searchTerm);
  }, [
    actualTrainerId,
    loadingEvents,
    loadingMore,
    mockInterviewEvents.length,
    pagination.total,
    recordsPerPage,
    searchTerm,
    fetchMockInterviews,
  ]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore]);

  // Changing the batch size starts the list again from the top rather than
  // working out which page the reader is now on. The fetch's own identity
  // moves with recordsPerPage, so the effect that reads it refetches.
  const handleRowsPerPageChange = useCallback((newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(Number(newLimit));
  }, []);

  const sizeRef = useRef(recordsPerPage);
  useEffect(() => {
    if (sizeRef.current === recordsPerPage) return;
    sizeRef.current = recordsPerPage;
    if (!actualTrainerId) return;
    appendRef.current = false;
    fetchMockInterviews(actualTrainerId, 1, searchTerm);
  }, [recordsPerPage, actualTrainerId, searchTerm, fetchMockInterviews]);

  // ─── Fetch batch students with mock interview evaluations ───────────────────
  const fetchModalBatchStudents = useCallback(async (bId, tId, eventId, page = 1, limit = 10, search = '', append = false) => {
    if (!bId || !tId) return;
    if (append) {
      setLoadingMoreStudents(true);
    } else {
      setBatchStudentsLoading(true);
    }
    try {
      const [studentsResp, resultsResp] = await Promise.all([
        axiosInstance.get(
          `/trainerdashboard/mystudents?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&batchId=${bId}&trainerId=${tId}`
        ),
        getMockInterviewResultsByEvent(eventId, bId, tId),
      ]);

      const rawStudents = studentsResp.data?.data || [];
      const paginationData = studentsResp.data?.pagination || {};
      const evaluations = Array.isArray(resultsResp) ? resultsResp : [];

      // Map evaluations by all possible student identifiers
      const evalMap = new Map();
      evaluations.forEach((ev) => {
        const rawId = ev.rawStudentId || ev.studentId;
        const sId = typeof rawId === 'string'
          ? rawId
          : Array.isArray(rawId)
            ? (rawId[0]?.id || rawId[0]?.studentId || rawId[0]?._id || rawId[0]?.userId)
            : (rawId?.id || rawId?._id || rawId?.studentId || rawId?.userId || '');
        if (sId) {
          evalMap.set(String(sId), ev);
        }
        if (ev.student?.id) evalMap.set(String(ev.student.id), ev);
        if (ev.student?.studentId) evalMap.set(String(ev.student.studentId), ev);
        if (ev.student?.userId) evalMap.set(String(ev.student.userId), ev);
        if (ev.student?.email) evalMap.set(String(ev.student.email).trim().toLowerCase(), ev);
      });

      const startSrNo = append ? (page - 1) * limit : 0;
      const enriched = rawStudents.map((s, idx) => {
        const sEmail = String(s.email || '').trim().toLowerCase();
        const evalData =
          (s.id ? evalMap.get(String(s.id)) : null) ||
          (s.studentId ? evalMap.get(String(s.studentId)) : null) ||
          (s.userId ? evalMap.get(String(s.userId)) : null) ||
          (s._id ? evalMap.get(String(s._id)) : null) ||
          (sEmail ? evalMap.get(sEmail) : null);

        return {
          ...s,
          srno: startSrNo + idx + 1,
          communicationMark: evalData?.communicationMark != null ? evalData.communicationMark : null,
          technicalMark: evalData?.technicalMark != null ? evalData.technicalMark : null,
          mark: evalData?.mark != null ? evalData.mark : (evalData?.communicationMark != null && evalData?.technicalMark != null ? (Number(evalData.communicationMark) + Number(evalData.technicalMark)) : null),
          totalMark: evalData?.totalMark != null ? evalData.totalMark : 20,
          result: evalData?.result || null,
          evalDescription: evalData?.description || evalData?.evalDescription || '',
          isEvaluated: !!(evalData && (evalData.id || evalData._id || evalData.mark != null || evalData.result || evalData.communicationMark != null)),
          evalData: evalData || null,
        };
      });

      if (append) {
        setBatchStudents((prev) => [...prev, ...enriched]);
      } else {
        setBatchStudents(enriched);
      }
      setTotalBatchStudents(paginationData.total || rawStudents.length);
    } catch (err) {
      console.error('Failed to load batch students with evaluations:', err);
    } finally {
      setBatchStudentsLoading(false);
      setLoadingMoreStudents(false);
    }
  }, []);

  // Sync batch students when modal opens or search changes
  useEffect(() => {
    if (showBatchModal && selectedBatch) {
      const bId = getBatchFromEvent(selectedBatch)?.id;
      if (bId && actualTrainerId) {
        setBatchStudentsPage(1);
        fetchModalBatchStudents(
          bId,
          actualTrainerId,
          selectedBatch.id,
          1,
          batchStudentsPerPage,
          appliedBatchStudentsSearch,
          false
        );
      }
    } else if (!showBatchModal) {
      setBatchStudents([]);
      setBatchStudentsSearch('');
      setAppliedBatchStudentsSearch('');
      setBatchStudentsPage(1);
      setTotalBatchStudents(0);
    }
  }, [
    showBatchModal,
    selectedBatch,
    actualTrainerId,
    appliedBatchStudentsSearch,
    batchStudentsPerPage,
    fetchModalBatchStudents,
  ]);

  const handleBatchStudentSearchChange = (val) => {
    setBatchStudentsSearch(val);
    if (batchStudentsSearchDebounceRef.current) {
      clearTimeout(batchStudentsSearchDebounceRef.current);
    }
    batchStudentsSearchDebounceRef.current = setTimeout(() => {
      setAppliedBatchStudentsSearch(val);
      setBatchStudentsPage(1);
    }, 500);
  };

  const handleLoadMoreStudents = () => {
    if (!selectedBatch || loadingMoreStudents || batchStudentsLoading) return;
    const bId = getBatchFromEvent(selectedBatch)?.id;
    if (!bId || !actualTrainerId) return;
    const nextPage = batchStudentsPage + 1;
    setBatchStudentsPage(nextPage);
    fetchModalBatchStudents(
      bId,
      actualTrainerId,
      selectedBatch.id,
      nextPage,
      batchStudentsPerPage,
      appliedBatchStudentsSearch,
      true
    );
  };

  const handleOpenStudentEvaluation = (student) => {
    setSelectedStudentData({
      student: student,
      batchId: getBatchFromEvent(selectedBatch)?.id,
      eventdata: selectedBatch,
      actualTrainerId,
    });
    setShowStudentModal(true);
  };

  const handleEvaluationSaved = useCallback(() => {
    if (selectedBatch) {
      const bId = getBatchFromEvent(selectedBatch)?.id;
      if (bId && actualTrainerId) {
        const countToFetch = Math.max(10, batchStudents.length);
        fetchModalBatchStudents(
          bId,
          actualTrainerId,
          selectedBatch.id,
          1,
          countToFetch,
          appliedBatchStudentsSearch,
          false
        );
      }
    }
  }, [
    selectedBatch,
    actualTrainerId,
    batchStudents.length,
    appliedBatchStudentsSearch,
    fetchModalBatchStudents,
  ]);

  // A change made in a dialog here is confirmed in that dialog, not a toast:
  // a delete in the session dialog, a save in the event form.
  const { done: batchDone } = usePopupDone(showBatchModal);
  const { done: eventFormDone } = usePopupDone(Boolean(eventFormId));

  // ─── Loading / error states ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div>
        <div>
          <div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <h4>Loading your Mock Interview section...</h4>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div>
          <div>
            <div><FaInfoCircle/></div>
            <div>
              <h4>Dashboard Temporarily Unavailable</h4>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* Admin, nobody chosen yet.

     This return fires BEFORE the main render, so it -- not the gate
     further down -- is what an admin actually lands on. It used to be
     six nested bare <div>s around a react-bootstrap Form.Group, none of
     which this project styles, which is why this page looked like it
     had no picker design while every other "pick someone" page had
     one. It is the shared pg-gate now, same as /student-profile,
     /student-quizzes, /ranking-dashboard and /mock-interviews. */
  if (isAdmin && !actualTrainerId) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="pg-gate">
            <span className="pg-gate-mark">
              <FaUserTie size={20} aria-hidden="true" />
            </span>
            <h1 className="pg-gate-title">Pick a trainer</h1>
            <p className="pg-gate-text">
              Choose a trainer to see the mock interviews scheduled for them.
            </p>

            <div className="pg-gate-field">{trainerPicker}</div>

            <span className="pg-gate-tip">
              <FaInfoCircle size={13} aria-hidden="true" />
              {trainerOptions.length > 0
                ? `${trainerOptions.length} trainers available`
                : 'Search by trainer name or email.'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render event row ─────────────────────────────────────────────────────────
  /* The five the row is allowed to show. A job board's listing is a name
     and a handful of columns that line up down the page; everything else
     is a click away, and the reader scans the five rather than reading
     six cards' worth of prose.

     Always these five, in this order, even when a session has no course
     or no duration -- a blank cell keeps the column straight, where
     dropping the row would shunt Trainer under Duration on one line and
     not the next and there would be no columns left to scan.

     Date, mode and Completed are not among them: the date sits under the
     name because it is part of what the session IS, and the other two
     are one word each and read better as marks than as columns.
     Description is the one that leaves -- it is free text, it has no
     business in a column, and the dialog has room for it. */
  const CARD_FACTS = 5;

  const renderEventCard = (event, isActive) => {
    const batch = getBatchFromEvent(event);
    const course = (Array.isArray(batch?.courseId) ? batch?.courseId[0] : batch?.courseId) || {};
    const trainerDisplayName = getTrainerNameFromEvent(event);

    const facts = [
      { key: 'batch', label: 'Batch', value: batch?.name },
      { key: 'course', label: 'Course', value: course.name },
      { key: 'duration', label: 'Duration', value: course.duration },
      {
        key: 'time',
        label: 'Time',
        /* Guarded: moment.utc(undefined) is NOW, so a session with no
           time on it printed the hour the page happened to be opened --
           a column has to be trustworthy or it is worse than blank. */
        value: event.time ? formatTime(event.time) : '',
        icon: <FaClock aria-hidden="true" />,
      },
      {
        key: 'trainer',
        label: 'Trainer',
        /* The helper's own "none" is the string N/A. In a column of five
           it has to be the same mark as the other four. */
        value: trainerDisplayName === 'N/A' ? '' : trainerDisplayName,
      },
    ].slice(0, CARD_FACTS);

    return (
      /* The whole row opens the detail dialog -- the actions that used
         to sit along its foot live in that dialog now, so there is
         nothing else on the row to click and the row itself is the
         target. role/tabIndex/onKeyDown because a <div> that behaves
         like a button has to say so and answer the keyboard. */
      <article
        className={`mk-row mk-row--open${isActive ? '' : ' mk-row--done'}`}
        key={eventRowKey(event)}
        role="button"
        tabIndex={0}
        aria-label={`Open ${event.name}`}
        onClick={() => handleBatchClick(event)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBatchClick(event);
          }
        }}
      >
        <div className="mk-row-id">
          <span className="mk-row-mark">
            {isActive ? <FaGraduationCap /> : <FaTrophy />}
          </span>
          <div className="mk-row-name">
            <h3 className="mk-row-title" title={event.name}>{event.name}</h3>
            <span className="mk-row-date">
              <FaCalendarAlt aria-hidden="true" />
              {formatEventDate(event.date)}
            </span>
          </div>
        </div>

        <dl className="mk-cols">
          {facts.map((f) => (
            <div className="mk-col" key={f.key}>
              <dt className="mk-col-label">{f.label}</dt>
              {/* An em dash rather than nothing: an empty cell under a
                  label reads as a rendering fault. pg-tip carries the
                  whole value for the ones a column has to cut, and is
                  left off the dash -- there is nothing to reveal.

                  The text is its own span because the ellipsis and the
                  tooltip cannot be the same element: clipping needs
                  overflow:hidden, and the tooltip is drawn above the
                  cell, where overflow:hidden would cut it off. */}
              <dd
                className={
                  f.value ? 'mk-col-value pg-tip' : 'mk-col-value mk-col-value--none'
                }
                data-tip={f.value ? String(f.value) : undefined}
              >
                {f.value && f.icon}
                <span className="mk-col-text">{f.value || '—'}</span>
              </dd>
            </div>
          ))}
        </dl>

        <div className="mk-row-rail">
          {batch?.mode && <span className="mk-card-mode">{batch.mode}</span>}
          {!isActive && <span className="mk-done-pill">Completed</span>}
          <FaChevronRight className="mk-row-go" aria-hidden="true" />
        </div>
      </article>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-wrapper">
      <div className="td-page">

        {isAdmin && selectedTrainerOption && (
          <section className="pg-filters" aria-label="Admin view">
            <div className="pg-filters-row">
              <div className="pg-field pg-field--grow">
                <label className="pg-label">Viewing trainer</label>
                {trainerPicker}
              </div>

              <button
                type="button"
                className="pg-btn"
                onClick={() => handleTrainerSelection(null)}
              >
                <FaTimes size={12} aria-hidden="true" />
                Clear
              </button>
            </div>
          </section>
        )}

        <header className="pg-head">
          <div className="pg-head-main">
            <h1 className="pg-title">
              Mock Interviews
              {trainerName && <span className="pg-title-sub"> — {trainerName}</span>}
            </h1>
          </div>

          <div className="pg-head-actions">
            <div className="td-search td-search--sm">
              <FaSearch className="td-search-icon" aria-hidden="true" />
              <input
                className="td-search-input"
                type="text"
                placeholder="Search by batch, course, or event name..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                aria-label="Search mock interviews"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="td-search-clear"
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <div className="pg-field pg-field--tight">
              <label className="pg-label" htmlFor="mk-rows">
                Rows per load
              </label>
              <select
                id="mk-rows"
                className="pg-select"
                value={recordsPerPage}
                onChange={(e) => handleRowsPerPageChange(e.target.value)}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </header>

        {loadingEvents && (
          <div className="pg-state">
            <span className="pg-spinner" role="status" aria-hidden="true" />
            <p className="pg-state-text">Loading mock interviews...</p>
          </div>
        )}

        {!loadingEvents && activeEvents.length > 0 && (
          <section className="mk-section">
            <div className="mk-section-head">
              <span className="mk-section-mark"><FaRocket /></span>
              <div className="mk-section-text">
                <h2 className="mk-section-title">
                  Upcoming
                  <span className="mk-section-count">{activeEvents.length}</span>
                </h2>
                <p className="mk-section-sub">Scheduled mock interview sessions</p>
              </div>
            </div>
            <div className="mk-list">
              {activeEvents.map((event) => renderEventCard(event, true))}
            </div>
          </section>
        )}

        {!loadingEvents && completedEvents.length > 0 && (
          <section className="mk-section mk-section--done">
            <div className="mk-section-head">
              <span className="mk-section-mark"><FaTrophy /></span>
              <div className="mk-section-text">
                <h2 className="mk-section-title">
                  Completed
                  <span className="mk-section-count">{completedEvents.length}</span>
                </h2>
                <p className="mk-section-sub">Previously conducted sessions</p>
              </div>
            </div>
            <div className="mk-list">
              {completedEvents.map((event) => renderEventCard(event, false))}
            </div>
          </section>
        )}

        {!loadingEvents && searchTerm && activeEvents.length === 0 && completedEvents.length === 0 && (
          <div className="pg-state">
            <span className="pg-state-icon"><FaSearch size={22} aria-hidden="true" /></span>
            <h3 className="pg-state-title">Nothing matched</h3>
            <p className="pg-state-text">
              No mock interviews match &ldquo;{searchTerm}&rdquo;.
            </p>
            <button type="button" className="pg-btn" onClick={handleClearSearch}>
              <FaTimes size={12} aria-hidden="true" /> Clear search
            </button>
          </div>
        )}

        {!loadingEvents && !searchTerm && mockInterviewEvents.length === 0 && (
          <div className="pg-state">
            <span className="pg-state-icon"><FaVideo size={22} aria-hidden="true" /></span>
            <h3 className="pg-state-title">No mock interviews scheduled</h3>
            <p className="pg-state-text">
              Once a session is scheduled it will appear here.
            </p>
          </div>
        )}

        {/* Where the reader is, and the sentinel the observer watches --
            crossing it fetches the next batch. Fixed height whether it is
            loading or finished, so a batch arriving never shunts the cards
            above it. Nothing at all for an empty list. */}
        {!loadingEvents && mockInterviewEvents.length > 0 && (
          <div className="pg-scroll-foot" ref={listEndRef}>
            {mockInterviewEvents.length < pagination.total ? (
              <>
                <span className="pg-scroll-count">
                  {mockInterviewEvents.length} of {pagination.total}
                </span>
                <span
                  className={`pg-spinner${loadingMore ? '' : ' pg-spinner--slot'}`}
                  aria-hidden="true"
                />
              </>
            ) : (
              pagination.total > 0 && (
                <span className="pg-scroll-count">
                  All {pagination.total} session{pagination.total === 1 ? '' : 's'} loaded
                </span>
              )
            )}
          </div>
        )}

        {/* ── Batch Detail Modal ──────────────────────────────────────────────── */}
        {/* react-bootstrap ships no CSS, and every modal rule in
            popups.css is scoped to a wrapper class this dialog never
            carried -- so it had no surface, padding or radius at all.
            .mk-modal is what gives it one. */}
        <Modal
          show={showBatchModal}
          onHide={() => setShowBatchModal(false)}
          dialogClassName="mk-modal"
          centered
          scrollable
          /* The evaluation dialog opens on top of this one and stays outside
             it; with the focus trap on, every click into its feedback textarea
             was pulled back here and nothing could be typed. */
          enforceFocus={!showStudentModal}
          /* Escape belongs to the dialog on top; it must not close this one
             from underneath it. */
          keyboard={!showStudentModal}
        >
          <Modal.Header>
            <div className="mk-modal-id">
              <span className="mk-modal-mark">
                <FaInfoCircle />
              </span>
              <div>
                <Modal.Title>
                  {selectedBatch?.name}
                  {getBatchFromEvent(selectedBatch)?.name && (
                    <small className="mk-modal-batch">
                      Batch: {getBatchFromEvent(selectedBatch)?.name}
                    </small>
                  )}
                </Modal.Title>
                <p className="mk-modal-sub">
                  Batch overview and student evaluations
                </p>
              </div>
            </div>
            <button
              type="button"
              className="mk-modal-close"
              aria-label="Close"
              onClick={() => setShowBatchModal(false)}
            >
              <FaTimes />
            </button>
          </Modal.Header>

          <Modal.Body>
            {batchDone ? (
              <PopupDone message={batchDone} onClose={() => setShowBatchModal(false)} />
            ) : selectedBatch && (() => {
              const batch = getBatchFromEvent(selectedBatch);
              const course = batch?.courseId?.[0] || {};
              return (
                <div>
                  {/* Every action for this session lives here now -- the
                      cards behind carry none, so this dialog is the one
                      place they are. */}
                  <div className="mkm-acts">
                    {selectedBatch.onlineLink && (
                      <a
                        className="mk-btn mk-btn--join"
                        href={selectedBatch.onlineLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaVideo /> Join mock interview
                      </a>
                    )}

                    {hasPermission('list-event-form', 'update', 'lms') && (
                      <button
                        type="button"
                        className="mk-btn"
                        onClick={() => handleEditEvent(selectedBatch.id)}
                      >
                        <FaEdit /> Edit
                      </button>
                    )}

                    {hasPermission('list-event-form', 'delete', 'lms') && (
                      <button
                        type="button"
                        className="mk-btn mk-btn--danger"
                        onClick={() => handleDeleteEvent(selectedBatch.id)}
                      >
                        <FaTrash /> Delete
                      </button>
                    )}
                  </div>

                  {/* Two halves rather than one long scroll. The facts
                      about the session and the roster of people in it are
                      different questions, and stacking them meant reading
                      past all of one to reach the other. Same lp-tabs the
                      event list and the dashboards use. */}
                  <div className="lp-tabs" role="tablist" aria-label="Session">
                    {[
                      { key: 'details', label: 'Details' },
                      { key: 'students', label: 'Students', n: totalBatchStudents },
                    ].map((t) => (
                      <button
                        type="button"
                        key={t.key}
                        role="tab"
                        id={`mk-batch-tab-${t.key}`}
                        aria-selected={batchModalTab === t.key}
                        aria-controls={`mk-batch-panel-${t.key}`}
                        className={`lp-tab${batchModalTab === t.key ? ' is-on' : ''}`}
                        onClick={() => setBatchModalTab(t.key)}
                      >
                        {t.label}
                        {t.n !== undefined && <span className="lp-tab-n">{t.n}</span>}
                      </button>
                    ))}
                  </div>

                  <section
                    className="mkm-section"
                    role="tabpanel"
                    id="mk-batch-panel-details"
                    aria-labelledby="mk-batch-tab-details"
                    hidden={batchModalTab !== 'details'}
                  >
                    <h4 className="mkm-section-title">Session</h4>
                    <div className="mkm-summary">
                      <span className="mkm-summary-mark">
                        <FaBookmark />
                      </span>
                      <div className="mkm-summary-text">
                        <h5 className="mkm-summary-name">{selectedBatch.name}</h5>
                        <p className="mkm-summary-sub">
                          {/* Waypoint. The batch and the course were flat
                              text -- the two records a session is actually
                              about, named and unreachable. */}
                          {batch?.name && (
                            <>
                              Batch:{' '}
                              {batch?.id ? (
                                <button
                                  type="button"
                                  className="wp-link"
                                  onClick={() => openEntity('batch', batch.id)}
                                >
                                  {batch.name}
                                </button>
                              ) : (
                                batch.name
                              )}
                            </>
                          )}
                          {course?.name && (
                            <>
                              {batch?.name ? ' · ' : ''}Course:{' '}
                              {course?.id ? (
                                <button
                                  type="button"
                                  className="wp-link"
                                  onClick={() => openEntity('course', course.id)}
                                >
                                  {course.name}
                                </button>
                              ) : (
                                course.name
                              )}
                              {course?.duration ? ` (${course.duration})` : ''}
                            </>
                          )}
                        </p>
                      </div>
                      {batch?.mode && <span className="mk-card-mode">{batch.mode}</span>}
                    </div>

                    <dl className="mkm-facts">
                      <div className="mkm-fact">
                        <dt>Date</dt>
                        <dd>{formatEventDate(selectedBatch.date)}</dd>
                      </div>
                      <div className="mkm-fact">
                        <dt>Time</dt>
                        <dd>{formatTime(selectedBatch.time)}</dd>
                      </div>
                      <div className="mkm-fact">
                        <dt>Venue</dt>
                        <dd className="pg-tip" data-tip={selectedBatch.offlineAddress || selectedBatch.classroomNumber || 'N/A'}>
                          {selectedBatch.offlineAddress || selectedBatch.classroomNumber || 'N/A'}
                        </dd>
                      </div>
                      <div className="mkm-fact">
                        <dt>Trainer</dt>
                        {/* An event can carry more than one. Each is its own
                            Waypoint rather than one joined string, so a
                            session run by two people reaches both. */}
                        <dd className="pg-tip" data-tip={getTrainerNameFromEvent(selectedBatch)}>
                          {Array.isArray(selectedBatch.trainerId) && selectedBatch.trainerId.length > 0
                            ? selectedBatch.trainerId.map((t, i) => (
                                <span key={t?.id || t?.trainerName || i}>
                                  {i > 0 && ', '}
                                  {t?.id ? (
                                    <button
                                      type="button"
                                      className="wp-link"
                                      onClick={() => openEntity('trainer', t.id)}
                                    >
                                      {t.trainerName}
                                    </button>
                                  ) : (
                                    t?.trainerName || 'N/A'
                                  )}
                                </span>
                              ))
                            : 'N/A'}
                        </dd>
                      </div>
                      <div className="mkm-fact">
                        <dt>Agenda</dt>
                        <dd className="pg-tip" data-tip={selectedBatch.agenda || 'N/A'}>
                          {selectedBatch.agenda || 'N/A'}
                        </dd>
                      </div>
                      <div className="mkm-fact">
                        <dt>Organizer</dt>
                        <dd className="pg-tip" data-tip={selectedBatch.organizerName || 'N/A'}>
                          {selectedBatch.organizerName || 'N/A'}
                        </dd>
                      </div>
                    </dl>

                    {/* The one fact the row gave up. It is free text of
                        any length, which is exactly what a column cannot
                        hold; here it gets the full width and as many
                        lines as it needs. */}
                    {selectedBatch.description && selectedBatch.description !== 'nil' && (
                      <div className="mkm-note">
                        <h5 className="mkm-note-label">Description</h5>
                        <p className="mkm-note-text">{selectedBatch.description}</p>
                      </div>
                    )}
                  </section>

                  <section
                    className="mkm-section"
                    role="tabpanel"
                    id="mk-batch-panel-students"
                    aria-labelledby="mk-batch-tab-students"
                    /* hidden, not unmounted: the roster owns a search box,
                       a page and a scroll position, and remounting it on
                       every tab flip would throw all three away and refire
                       the fetch. */
                    hidden={batchModalTab !== 'students'}
                  >
                    <div className="mkm-section-head">
                      <h4 className="mkm-section-title">
                        <FaUsers aria-hidden="true" />
                        Students in this batch
                        <span className="mk-section-count">{totalBatchStudents}</span>
                      </h4>

                      {/* mkm-search, NOT the kit's td-search: every td-*
                          rule is written as `.dashboard-wrapper .td-…`,
                          and react-bootstrap portals this dialog to
                          document.body -- outside that wrapper -- so the
                          kit's search would have had no styling at all
                          in here. The pg-* classes used elsewhere in this
                          dialog are unscoped, which is why they do work. */}
                      <div className="mkm-search">
                        <FaSearch className="mkm-search-icon" aria-hidden="true" />
                        <input
                          className="mkm-search-input"
                          type="text"
                          placeholder="Search student..."
                          value={batchStudentsSearch}
                          onChange={(e) => handleBatchStudentSearchChange(e.target.value)}
                          aria-label="Search students in this batch"
                        />
                        {batchStudentsSearch && (
                          <button
                            type="button"
                            className="mkm-search-clear"
                            aria-label="Clear search"
                            onClick={() => handleBatchStudentSearchChange('')}
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    </div>

                    {batchStudentsLoading ? (
                      <div className="pg-state">
                        <span className="pg-spinner" role="status" aria-hidden="true" />
                        <p className="pg-state-text">Loading students and scores...</p>
                      </div>
                    ) : batchStudents.length > 0 ? (
                      <>
                        <div className="mkm-table-wrap">
                          <table className="mkm-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Student</th>
                                <th>Communication</th>
                                <th>Technical</th>
                                <th>Total</th>
                                <th>Status</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batchStudents.map((s) => (
                                <tr key={s.id || s.studentId || s.userId || s.srno}>
                                  <td>{s.srno}</td>
                                  <td>
                                    {/* Waypoint. A name in a roster is a
                                        student, and from them their batches,
                                        courses and attendance -- the reason
                                        the bridge exists. */}
                                    {s.id || s.studentId || s.userId ? (
                                      <button
                                        type="button"
                                        className="wp-link mkm-student-link"
                                        onClick={() =>
                                          openEntity('student', s.id || s.studentId || s.userId)
                                        }
                                      >
                                        {s.name || s.studentName || 'Unnamed student'}
                                      </button>
                                    ) : (
                                      <span className="mkm-student">
                                        {s.name || s.studentName || 'Unnamed student'}
                                      </span>
                                    )}
                                    <span className="mkm-student-meta">
                                      {s.email}
                                      {(s.contactNo || s.phone) && ` · ${s.contactNo || s.phone}`}
                                    </span>
                                  </td>
                                  <td>
                                    {s.communicationMark != null ? (
                                      <span className="mkm-mark">
                                        <FaComments aria-hidden="true" /> {s.communicationMark}
                                      </span>
                                    ) : (
                                      <span className="mkm-na">NA</span>
                                    )}
                                  </td>
                                  <td>
                                    {s.technicalMark != null ? (
                                      <span className="mkm-mark">
                                        <FaLaptopCode aria-hidden="true" /> {s.technicalMark}
                                      </span>
                                    ) : (
                                      <span className="mkm-na">NA</span>
                                    )}
                                  </td>
                                  <td>
                                    {s.mark != null ? (
                                      <span className="mkm-total">
                                        {s.mark}
                                        <span className="mkm-total-of"> / {s.totalMark || 20}</span>
                                      </span>
                                    ) : (
                                      <span className="mkm-na">NA</span>
                                    )}
                                  </td>
                                  <td>
                                    {s.isEvaluated ? (
                                      s.result === 'Qualified' ? (
                                        <span className="mkm-pill mkm-pill--ok">
                                          <FaCheckCircle aria-hidden="true" /> Qualified
                                        </span>
                                      ) : s.result === 'Unqualified' ? (
                                        <span className="mkm-pill mkm-pill--bad">
                                          <FaTimesCircle aria-hidden="true" /> Unqualified
                                        </span>
                                      ) : (
                                        <span className="mkm-pill">{s.result}</span>
                                      )
                                    ) : (
                                      <span className="mkm-pill mkm-pill--wait">Pending</span>
                                    )}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="mk-btn mk-btn--main"
                                      onClick={() => handleOpenStudentEvaluation(s)}
                                    >
                                      {s.isEvaluated ? (
                                        <>
                                          <FaEdit aria-hidden="true" /> Edit marks
                                        </>
                                      ) : (
                                        <>
                                          <FaAward aria-hidden="true" /> Evaluate
                                        </>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {totalBatchStudents > batchStudents.length && (
                          <div className="mkm-more">
                            <small className="mkm-more-count">
                              Showing {batchStudents.length} of {totalBatchStudents}
                            </small>
                            <button
                              type="button"
                              className="mk-btn"
                              disabled={loadingMoreStudents}
                              onClick={handleLoadMoreStudents}
                            >
                              {loadingMoreStudents ? (
                                'Loading...'
                              ) : (
                                <>
                                  <FaChevronDown aria-hidden="true" /> Show more
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        {totalBatchStudents <= batchStudents.length && totalBatchStudents > 10 && (
                          <div className="mkm-more">
                            <small className="mkm-more-count">
                              All {totalBatchStudents} students loaded
                            </small>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="pg-state">
                        <span className="pg-state-icon">
                          <FaUsers size={22} aria-hidden="true" />
                        </span>
                        <h3 className="pg-state-title">No students found</h3>
                        <p className="pg-state-text">
                          {appliedBatchStudentsSearch
                            ? 'Try a different search term.'
                            : 'Nobody is enrolled in this batch yet.'}
                        </p>
                      </div>
                    )}
                  </section>
                </div>
              );
            })()}
          </Modal.Body>
          {/* A bare <button>: no class at all, so with no Bootstrap
              stylesheet it rendered as the browser's default grey chip.
              pg-btn--main is what Close is everywhere else in this app. */}
          {!batchDone && (
            <Modal.Footer className="mk-modal-foot">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setShowBatchModal(false)}
              >
                Close
              </button>
            </Modal.Footer>
          )}
        </Modal>

        {/* ── Edit event ──────────────────────────────────────────────────────
            The same AddEventModal /list-event-Form edits through, in the same
            pg-modal frame it uses there -- so editing a mock interview from
            here and editing the underlying event from the list are one
            dialog, not two implementations that drift. */}
        {eventFormId && (
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mk-ev-form-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setEventFormId(null);
            }}
          >
            <div className="pg-modal-panel pg-modal-panel--xl pg-modal-panel--form">
              <header className="pg-modal-head">
                <div className="pg-modal-heading">
                  <span className="pg-modal-eyebrow">Mock interview</span>
                  <h3 className="pg-modal-title" id="mk-ev-form-title">
                    Edit event
                  </h3>
                </div>
                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={() => setEventFormId(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                {eventFormDone ? (
                  <PopupDone message={eventFormDone} onClose={() => setEventFormId(null)} />
                ) : (
                  <AddEventModal
                    selectedEventId={eventFormId}
                    selectedDate={null}
                    userRole={isAdmin ? 'admin' : 'trainer'}
                    onCancel={() => setEventFormId(null)}
                    onEventSaved={() => {
                      // Saved: the dialog shows so in place and closes itself.
                      // Rebuild the list so an edited session shows its new
                      // values without a reload.
                      if (actualTrainerId) {
                        fetchMockInterviews(actualTrainerId, 1, searchTerm);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Student Detail Modal ────────────────────────────────────────────── */}
        <StudentDetailModal
          show={showStudentModal}
          onHide={() => setShowStudentModal(false)}
          studentData={selectedStudentData}
          onEvaluationSaved={handleEvaluationSaved}
          /* The only way into this dialog is the roster's Evaluate / Edit
             marks button, so it opens ready to take the marks. Without this
             an already-evaluated student arrived on a read-only panel with
             an "Edit evaluation" button -- the same question, asked twice. */
          editOnOpen
        />
      </div>
    </div>
  );
};

export default MockInterview;